import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Card, Form } from "react-bootstrap";
import { Button } from '@blueprint/ui';
import * as yup from "yup";
import styled from "styled-components";

import { Loader, Table } from "../../../common/components/core";
import {
    FormFooter,
    SelectField,
} from "../../../common/components/form";
import { SelectOption } from "../../../common/components/form/Select";
import { FormSubmissionHandler } from "../../../types/common";
import relationshipApi from "../../../api/relationship";
import { UserProfile } from "../../../api/profile";
import { UserProfileRelationship } from "../../../types/relationship";


export type RelationshipFormValues = {
    id: number;
    developer: string;
    contractorList: string[];
    approverList: string[];
};

const schema: yup.SchemaOf<RelationshipFormValues> = yup.object().shape({
    id: yup.number().required(),
    developer: yup.string().required(),
    contractorList: yup.array().min(1),
    approverList: yup.array().min(1),
});

type RelationshipFormProps = {
    onCancel: () => void;
    onSubmit: FormSubmissionHandler<RelationshipFormValues>;
    relationship: UserProfileRelationship;
};

const obj2string = (userProfile: UserProfile): string => {
    return JSON.stringify({
        id: userProfile.id,
        contactPersonEmail: userProfile.contactPersonEmail,
        contactPersonName: userProfile.contactPersonName,
        contactPersonNumber: userProfile.contactPersonNumber,
        location: userProfile.location,
        name: userProfile.name,
        type: userProfile.type,
        accountName: userProfile.accountName,
        wallet: userProfile.wallet,
    });
}

const RelationshipForm = ({
    onCancel,
    onSubmit,
    relationship
}: RelationshipFormProps): JSX.Element => {

    const [ contractorOptions, setContractorOptions ] = useState<SelectOption[]>([]);
    const [ approverOptions, setApproverOptions ] = useState<SelectOption[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getList = async () => {
            setIsLoading(true)
            try {
                const immutableContractorList = await relationshipApi.immutableContractorList(relationship.developerUserProfile.id);
                const contractorList = await relationshipApi.listContractor();
                const contractorOptions: SelectOption[] = [];
                contractorList.map((it) => {
                    const disableField = immutableContractorList.find((ban) => ban.accountName === it.accountName)
                    if (!!it.accountName) {
                        if (!!disableField) {
                            contractorOptions.push({ value: obj2string(it), label: it.name, isDisabled: true })
                        } else {
                            contractorOptions.push({ value: obj2string(it), label: it.name })
                        }
                    }
                })
                setContractorOptions(contractorOptions);

                const immutableApproverList = await relationshipApi.immutableApproverList(relationship.developerUserProfile.id);
                const approverList = await relationshipApi.listApprovers();
                const approverOptions = approverList.map((it) => {
                    const disableField = immutableApproverList.find((ban) => ban.id === it.id)
                    return !!disableField ? { value: obj2string(it), label: it.name, isDisabled: true } : { value: obj2string(it), label: it.name }
                })
                setApproverOptions(approverOptions);

            } catch (e) {
                console.error(e);
            }
            setIsLoading(false)
        };
        getList();
        return () => {
            setIsLoading(false)
        };
    }, [relationship]);

    const initialValues = {
        id: relationship.id ?? 0,
        developer: obj2string(relationship.developerUserProfile),
        contractorList: relationship.contractorAccountNameList?.map((it) => obj2string(it)) || [],
        approverList: relationship.approverUserProfileList?.map((it) => obj2string(it)) || [],
    }

    return (
        <Formik
            validationSchema={schema}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={onSubmit}
            initialValues={initialValues}
        >
            {({ handleSubmit, isSubmitting, setFieldValue, values }) => (
                <Loader isLoading={isLoading}>
                    <Form
                        autoComplete="off"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <Card className="mt-4 border-0">
                            <Card.Body className="px-0 py-0">
                                <table className={'custom-table'}>
                                    <thead>
                                        <tr>
                                            <Table.HeaderCell width={100}>
                                                Relationship
                                            </Table.HeaderCell>
                                            <Table.HeaderCell width={200}>
                                                Service Provider
                                            </Table.HeaderCell>
                                            <Table.HeaderCell width={200}>
                                                Approver
                                            </Table.HeaderCell>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <Table.BodyRow>
                                            <StyledTd>{relationship.developerUserProfile?.name}</StyledTd>
                                            <StyledTd>
                                                <SelectField
                                                    isMulti={true}
                                                    name="contractorList"
                                                    label=""
                                                    options={contractorOptions}
                                                    required={true}
                                                />
                                                {values.contractorList.map((it, index) => {
                                                    return <div key={index}>{JSON.parse(it).name}</div>
                                                })}
                                            </StyledTd>
                                            <StyledTd>
                                                <SelectField
                                                    isMulti={true}
                                                    name="approverList"
                                                    label=""
                                                    options={approverOptions}
                                                    required={true}
                                                />
                                                {values.approverList.map((it, index) => {
                                                    return <div key={index}>{JSON.parse(it).name}</div>
                                                })}
                                            </StyledTd>
                                        </Table.BodyRow>
                                    </tbody>
                                </table>
                            </Card.Body>
                        </Card>
                        <FormFooter className="d-flex justify-content-between">
                            <Button
                                variant="light.tertiary"
                                onClick={onCancel}
                            >
                                Cancel
                            </Button>
                            <Button variant="light.primary" type="submit" disabled={isLoading}>
                                Confirm
                            </Button>
                        </FormFooter>
                    </Form>
                </Loader>
            )}
        </Formik>
    );
};

const StyledTd = styled.td`
    vertical-align: top;
`;

export default RelationshipForm;
